import type { TrackingIntegration } from '@chukua/common';
import { Alert, Tag } from 'antd';
import clsx from 'clsx';
import { ChevronRight, Plus } from 'lucide-react';
import { Link } from 'react-router';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { IntegrationLogo } from '../../../components/IntegrationLogo';
import { addIntegrationPath, INTEGRATION_KIND_LABELS } from '../../../integrations.paths';
import { gpsIntegrationPath } from '../gps.paths';
import type { AdapterCatalogueState } from '../hooks/use-adapter-catalogue';
import { useGpsIntegrationCounts } from '../hooks/use-gps-integration-counts';
import type { GpsIntegrationsState } from '../hooks/use-gps-integrations';
import { gpsRowFacts } from '../policies/gps-row.policy';

interface GpsSectionProps {
  gps: GpsIntegrationsState;
  catalogue: AdapterCatalogueState;
}

/** The installed GPS accounts, each a link to its own page where its devices are put on trucks. */
export function GpsSection({ gps, catalogue }: GpsSectionProps) {
  return (
    <section aria-labelledby="gps-integrations" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 id="gps-integrations" className="sr-only">
          {INTEGRATION_KIND_LABELS.gps}
        </h2>
        <span className="text-sm text-muted">Where your trucks are. Install one per GPS account.</span>
      </div>
      <GpsSectionBody gps={gps} catalogue={catalogue} />
      <Link
        to={addIntegrationPath('gps')}
        className="flex items-center gap-2 rounded-xl border border-dashed border-line px-4 py-3 font-medium text-link hover:border-primary"
      >
        <Plus className="size-4" aria-hidden />
        Add a GPS integration
      </Link>
    </section>
  );
}

function GpsSectionBody({ gps, catalogue }: GpsSectionProps) {
  if (gps.status === 'loading') return <RowListSkeleton label="Loading GPS integrations" rows={1} />;
  if (gps.status !== 'loaded') return <Alert type="error" showIcon title="Couldn't load the GPS integrations. Try reloading the page." />;
  if (gps.integrations.length === 0) return <p className="text-sm text-muted">No GPS integration yet. Trucks show no live position until one is installed.</p>;

  const adapters = catalogue.status === 'loaded' ? catalogue.adapters : [];
  return (
    <ul className="divide-y divide-line rounded-xl border border-line bg-surface">
      {gps.integrations.map((integration) => (
        <GpsIntegrationRow
          key={integration.id}
          integration={integration}
          logoSvg={adapters.find((adapter) => adapter.key === integration.adapterKey)?.logoSvg}
        />
      ))}
    </ul>
  );
}

function GpsIntegrationRow({ integration, logoSvg }: { integration: TrackingIntegration; logoSvg: string | undefined }) {
  const counts = useGpsIntegrationCounts(integration.id);
  return (
    <li>
      <Link to={gpsIntegrationPath(integration.id)} className="flex items-center gap-4 px-4 py-3.5 hover:bg-primary/5">
        {logoSvg && <IntegrationLogo logoSvg={logoSvg} name={integration.adapterName} />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-fg">{integration.displayName}</span>
            {integration.status === 'enabled' ? <Tag color="success">On</Tag> : <Tag>Off</Tag>}
          </div>
          <p className="flex flex-wrap gap-x-3 text-sm text-muted">
            <span>{integration.adapterName}</span>
            {gpsRowFacts(integration, counts).map((fact) => (
              <span key={fact.text} className={clsx(fact.isWarning && 'font-medium text-warning')}>
                {fact.text}
              </span>
            ))}
          </p>
        </div>
        <ChevronRight className="size-4 text-muted" aria-hidden />
      </Link>
    </li>
  );
}
